import {
    eachDayOfInterval,
    format,
    startOfMonth,
    endOfMonth,
  } from 'date-fns';
  import config from '../../../../config';
  import { parseEvSensorData } from '../../../../utils/parseEvSensorData';
  import { generateColor } from '../utils/chartColors';
  
  const buildMonthList = (startDate, endDate) => {
    const months = [];
    let current = startOfMonth(startDate);
  
    while (current <= endDate) {
      months.push(format(current, 'yyyy-MM'));
      current = startOfMonth(new Date(current.getFullYear(), current.getMonth() + 1, 1));
    }
  
    return months;
  };
  
  const sumMonthPower = (rows, seq, monthStr) => {
    const monthStart = startOfMonth(new Date(`${monthStr}-01`));
    const monthEnd = endOfMonth(monthStart);
  
    return rows
      .filter((item) => {
        if (item.seq !== seq) return false;
        const itemDate = new Date(String(item.created_at || '').split(' ')[0]);
        return itemDate >= monthStart && itemDate <= monthEnd;
      })
      .reduce((sum, item) => sum + (Number(item.Iv_Add_power) || 0), 0);
  };
  
  export const fetchAndBuildMonthlyGeneration = async ({
    startMonth,
    endMonth,
    mode,
    ivList,
    Ev_list,
  }) => {
    const startDate = startOfMonth(startMonth);
    const endDate = endOfMonth(endMonth);
    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');
    const body = JSON.stringify({ start_date: startDateStr, end_date: endDateStr });
  
    const [ivRes, envRes] = await Promise.all([
      fetch(`${config.backendUrl}/get_day_Iv_data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      fetch(`${config.backendUrl}/get_hourly_Ev_mrt_data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    ]);
  
    let ivRows = [];
    if (ivRes.ok) {
      try {
        const data = await ivRes.json();
        ivRows = Array.isArray(data?.data) ? data.data : [];
      } catch (e) {
        console.warn('인버터 응답 JSON 파싱 실패:', e);
      }
    } else {
      console.warn(`인버터 요청 실패: ${ivRes.status} — 빈 데이터로 조회합니다.`);
    }
  
    let envData = { status: 'success', data: [] };
    if (envRes.ok) {
      try {
        envData = await envRes.json();
      } catch (e) {
        console.warn('환경센서 응답 JSON 파싱 실패:', e);
      }
    } else {
      console.warn(`환경센서 요청 실패: ${envRes.status} — 빈 데이터로 조회합니다.`);
    }
  
    const safeIvList = {
      seq: ivList?.seq || [],
      iv_name: ivList?.iv_name || [],
      iv_capa: ivList?.iv_capa || [],
    };
  
    const allDates = eachDayOfInterval({ start: startDate, end: endDate }).map((date) =>
      format(date, 'yyyy-MM-dd')
    );
    const allMonths = buildMonthList(startDate, endDate);
  
    let horizontalIrradiance = allDates.map(() => null);
    let tiltedIrradiance = allDates.map(() => null);
    let surfaceTemp = allDates.map(() => null);
    let outdoorTemp = allDates.map(() => null);
  
    try {
      const parsed = parseEvSensorData(envData, allDates, Ev_list);
      horizontalIrradiance = Array.isArray(parsed?.horizontalIrradiance)
        ? parsed.horizontalIrradiance
        : horizontalIrradiance;
      tiltedIrradiance = Array.isArray(parsed?.tiltedIrradiance)
        ? parsed.tiltedIrradiance
        : tiltedIrradiance;
      surfaceTemp = Array.isArray(parsed?.surfaceTemp) ? parsed.surfaceTemp : surfaceTemp;
      outdoorTemp = Array.isArray(parsed?.outdoorTemp) ? parsed.outdoorTemp : outdoorTemp;
    } catch (e) {
      console.warn('환경센서 데이터 파싱 실패:', e);
    }
  
    const dateIndexPairs = allDates.map((dateStr, i) => ({ i, date: new Date(dateStr) }));
  
    const getAvg = (arr, indices) =>
      indices.length
        ? indices
            .map((i) => arr[i])
            .filter((v) => v !== null && !isNaN(v))
            .reduce((sum, v) => sum + v, 0) / indices.length
        : 0;
  
    const avgHorizontal = [];
    const avgTilted = [];
    const avgSurfaceTemp = [];
    const avgOutdoorTemp = [];
  
    allMonths.forEach((monthStr) => {
      const indices = dateIndexPairs
        .filter(({ date }) => format(date, 'yyyy-MM') === monthStr)
        .map(({ i }) => i);
  
      avgHorizontal.push(Number(getAvg(horizontalIrradiance, indices).toFixed(2)));
      avgTilted.push(Number(getAvg(tiltedIrradiance, indices).toFixed(2)));
      avgSurfaceTemp.push(Number(getAvg(surfaceTemp, indices).toFixed(2)));
      avgOutdoorTemp.push(Number(getAvg(outdoorTemp, indices).toFixed(2)));
    });
  
    const totalCapacity = (safeIvList.iv_capa || []).reduce((sum, capa) => sum + (capa || 0), 0);
    let displayed = null;
    let csv = [];
  
    const envDatasets = [
      {
        label: '경사 일사량 평균',
        data: avgTilted,
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        fill: false,
        type: 'line',
        yAxisID: 'y1',
      },
      {
        label: '수평 일사량 평균',
        data: avgHorizontal,
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        fill: false,
        type: 'line',
        yAxisID: 'y1',
      },
      {
        label: '모듈온도 평균',
        data: avgSurfaceTemp,
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 2,
        fill: false,
        type: 'line',
        yAxisID: 'y2',
      },
      {
        label: '외기온도 평균',
        data: avgOutdoorTemp,
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 2,
        fill: false,
        type: 'line',
        yAxisID: 'y2',
      },
    ];
  
    if (mode === 'total') {
      const totalData = allMonths.map((month) =>
        (safeIvList.seq || []).reduce((sum, seq) => sum + sumMonthPower(ivRows, seq, month), 0)
      );
  
      displayed = {
        labels: allMonths,
        datasets: [
          {
            label: '전체 합계',
            data: totalData,
            backgroundColor: 'rgba(255, 99, 132, 0.6)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1,
            type: 'bar',
          },
          ...envDatasets,
        ],
      };
  
      csv = [
        [
          '날짜',
          '전체 합계 (kWh)',
          '발전시간 (h)',
          '경사 일사량 (W/m²)',
          '수평 일사량 (W/m²)',
          '모듈온도 (℃)',
          '외기온도 (℃)',
        ],
        ...allMonths.map((month, index) => [
          month,
          `${(totalData[index] / 1000).toFixed(2)} kWh`,
          `${(totalCapacity > 0 ? totalData[index] / totalCapacity / 1000 : 0).toFixed(2)} h`,
          `${avgTilted[index]} W/m²`,
          `${avgHorizontal[index]} W/m²`,
          `${avgSurfaceTemp[index]} ℃`,
          `${avgOutdoorTemp[index]} ℃`,
        ]),
      ];
  
      const totalPowerSum = totalData.reduce((sum, value) => sum + value, 0);
      const sumSafe = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) : 0);
  
      csv.push([
        '합계',
        `${(totalPowerSum / 1000).toFixed(2)} kWh`,
        `${(totalCapacity > 0 ? totalPowerSum / totalCapacity / 1000 : 0).toFixed(2)} h`,
        `${sumSafe(avgTilted)} W/m²`,
        `${sumSafe(avgHorizontal)} W/m²`,
        `${sumSafe(avgSurfaceTemp)} ℃`,
        `${sumSafe(avgOutdoorTemp)} ℃`,
      ]);
    } else {
      const inverterDatasets = (safeIvList.seq || []).map((seq, index) =>
        allMonths.map((month) => sumMonthPower(ivRows, seq, month))
      );
  
      displayed = {
        labels: allMonths,
        datasets: [
          ...(safeIvList.seq || []).map((seq, index) => ({
            label: safeIvList.iv_name[index] || `인버터 ${index + 1}`,
            data: inverterDatasets[index],
            backgroundColor: generateColor(index, safeIvList.seq.length || 1),
            borderColor: generateColor(index, safeIvList.seq.length || 1),
            borderWidth: 2,
            fill: false,
            yAxisID: 'y',
          })),
          ...envDatasets,
        ],
      };
  
      csv = [
        [
          '날짜',
          ...(safeIvList.iv_name || []).map((name) => `${name} (kWh)`),
          '전체 합계 (kWh)',
          '발전시간 (h)',
          '경사 일사량 (W/m²)',
          '수평 일사량 (W/m²)',
          '모듈온도 (℃)',
          '외기온도 (℃)',
        ],
        ...allMonths.map((month, index) => {
          const totalPower = inverterDatasets.reduce((sum, dataset) => sum + dataset[index], 0);
          return [
            month,
            ...inverterDatasets.map((dataset) => `${(dataset[index] / 1000).toFixed(2)} kWh`),
            `${(totalPower / 1000).toFixed(2)} kWh`,
            `${(totalCapacity > 0 ? totalPower / totalCapacity / 1000 : 0).toFixed(2)} h`,
            `${avgTilted[index]} W/m²`,
            `${avgHorizontal[index]} W/m²`,
            `${avgSurfaceTemp[index]} ℃`,
            `${avgOutdoorTemp[index]} ℃`,
          ];
        }),
      ];
  
      const columnSums = inverterDatasets.map((dataset) =>
        dataset.reduce((sum, value) => sum + value, 0)
      );
      const totalPowerSum = columnSums.reduce((sum, value) => sum + value, 0);
      const average = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0).toFixed(2);
  
      csv.push([
        '합계',
        ...columnSums.map((sum) => `${(sum / 1000).toFixed(2)} kWh`),
        `${(totalPowerSum / 1000).toFixed(2)} kWh`,
        `${(totalCapacity > 0 ? totalPowerSum / totalCapacity / 1000 : 0).toFixed(2)} h`,
        `${average(avgTilted)} W/m²`,
        `${average(avgHorizontal)} W/m²`,
        `${average(avgSurfaceTemp)} ℃`,
        `${average(avgOutdoorTemp)} ℃`,
      ]);
    }
  
    return { displayed, csv };
  };
  